import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, MapPin, ArrowLeft, User, LogOut, BarChart3, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRoadmap } from '@/contexts/roadmap';
import { useAuth } from '@/contexts/auth';
import { useWidget } from '@/contexts/WidgetContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const NavigationHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { templates } = useRoadmap();
  const { currentUser, logout } = useAuth();
  const { companyId, companyDetails } = useWidget();

  // Company branding
  const hasCompanyBranding = !!companyId && !!companyDetails;
  const companyLogo = companyDetails?.logo_url;
  const companyName = companyDetails?.name;

  // Build URLs with companyId for branded pages
  const homeUrl = companyId ? `/?companyId=${companyId}` : '/';
  const loginUrl = companyId ? `/login?companyId=${companyId}&mode=signin` : '/login';

  // Check if we're on an auth page
  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup';

  // Check if we're on a roadmap page
  const isRoadmapPage = location.pathname === '/roadmap';
  const searchParams = new URLSearchParams(location.search);
  const roadmapId = searchParams.get('id');

  // Get the active roadmap title if we're on a roadmap page
  const activeRoadmap = roadmapId ? templates.find(t => t.id === roadmapId)?.title : null;

  const handleLogout = async () => {
    await logout(navigate);
  };

  return <header className="sticky top-0 z-50 border-b border-cw bg-cw-card shadow-sm" data-tour="navigation-header">
    <div className="container mx-auto px-4">
      <div className="flex items-center justify-between h-16">
        {/* Logo and brand */}
        <Link to={homeUrl} className="flex items-center gap-2">
          {hasCompanyBranding && companyLogo ? (
            <img
              src={companyLogo}
              alt={companyName || 'Company logo'}
              className="h-8 w-auto max-w-[120px] object-contain"
            />
          ) : (
            <>
              <img
                src="/cloudwise-logo.svg"
                alt="CloudWise Academy"
                className="h-8 w-auto"
              />
            </>
          )}
        </Link>

        {/* Navigation */}
        <nav className="flex items-center gap-4">
          <div className="flex items-center space-x-1">
            {/* Show back button on roadmap page */}
            {isRoadmapPage && <Link to="/roadmaps" className="group flex items-center gap-1 rounded-md px-3 py-2 text-muted-foreground transition-colors hover:bg-cw-subtle hover:text-studio-900">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden md:inline-block">Back to Roadmaps</span>
            </Link>}

            {/* Navigation links */}
            <NavLink to={homeUrl} icon={<Home className="h-4 w-4" />} label="Home" />
          </div>

          {/* User menu */}
          {currentUser ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-2" data-tour="user-menu-button">
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline">{currentUser.name || 'User'}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  {currentUser.name || 'User'}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")}>
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Dashboard
                </DropdownMenuItem>
                {/* REMOVED ADMIN FEATURES FOR WIDGET */}
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            !isAuthPage && (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => navigate(loginUrl)}>
                  Login
                </Button>
              </div>
            )
          )}
        </nav>

        {/* Current roadmap indicator (on roadmap page) */}
        {isRoadmapPage && activeRoadmap && <div className="hidden lg:block">
          <span className="text-sm text-muted-foreground">
            Viewing: <span className="font-medium text-studio-900">{activeRoadmap}</span>
          </span>
        </div>}
      </div>
    </div>

    {/* Breadcrumb/path indicator for mobile (only on roadmap page) */}
    {isRoadmapPage && activeRoadmap && <div className="bg-cw-subtle py-2 md:hidden">
      <div className="container mx-auto px-4">
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Link to="/roadmaps" className="hover:text-studio-900">Roadmaps</Link>
          <span className="mx-1 text-muted-foreground/70">/</span>
          <span className="truncate font-medium text-studio-900">{activeRoadmap}</span>
        </div>
      </div>
    </div>}
  </header>;
};

// NavLink component for consistent styling
const NavLink = ({
  to,
  icon,
  label
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
}) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  return <Link to={to} className={cn("flex items-center gap-1 rounded-md px-3 py-2 transition-colors", isActive ? "bg-studio-100 font-medium text-studio-950" : "text-muted-foreground hover:bg-cw-subtle hover:text-studio-900")}>
    {icon}
    <span className="hidden md:inline-block">{label}</span>
  </Link>;
};
export default NavigationHeader;
